import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Cpu, RefreshCcw } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { LogoMark, Sparkline } from '../../../components/design-system/admin';
import { PROVIDER_NAMES, type ProviderView } from '../../../common/models/platform-costs.model';
import { KEY_PAGES, LevelBadge, money, moneyShort } from './cost-format';

/** Runway shown on the gauge tops out here: a month or more reads as full. */
const GAUGE_DAYS = 30;

const BAR: Record<string, string> = {
  ok: 'bg-foreground/70',
  low: 'bg-warning',
  critical: 'bg-destructive',
  unknown: 'bg-muted-foreground/40',
};

/**
 * One provider at a glance: what is left, how long it lasts, what it spends,
 * and the way to top it up. Not connected shows the env vars it is missing.
 */
export function ProviderCard({ p }: { p: ProviderView }) {
  const { t } = useTranslation();
  const name = PROVIDER_NAMES[p.provider];
  const connected = p.status !== 'unconfigured';

  const header = (
    <div className="flex items-start justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2.5">
        <LogoMark name={name} className="size-8" />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{name}</p>
          <LevelBadge provider={p} />
        </div>
      </div>
      <Button asChild variant="outline" size="sm" className="h-7 shrink-0 px-2 text-xs" data-testid={`costs-topup-${p.provider}`}>
        <a href={p.topUpUrl} target="_blank" rel="noreferrer">
          {p.balanceSource === 'provider' ? t('ADMIN_COSTS.CARD.TOP_UP') : t('ADMIN_COSTS.CARD.OPEN')}
          <ArrowUpRight className="size-3.5" />
        </a>
      </Button>
    </div>
  );

  if (!connected) {
    return (
      <Card className="h-full gap-0 px-4 py-4" data-testid={`costs-provider-${p.provider}`}>
        {header}
        <p className="mt-4 text-xs text-muted-foreground">{t('ADMIN_COSTS.CARD.CONNECT')}</p>
        <ul className="mt-1.5 flex flex-col gap-1">
          {(p.missing || []).map((v) => (
            <li key={v}>
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">{v}</code>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">{t(`ADMIN_COSTS.CONNECT_HINT.${p.provider}`)}</p>
        <a href={KEY_PAGES[p.provider]} target="_blank" rel="noreferrer" className="mt-auto inline-flex items-center gap-1 pt-3 text-xs text-foreground underline-offset-4 hover:underline">
          {t('ADMIN_COSTS.CARD.WHERE')}
          <ArrowUpRight className="size-3" />
        </a>
      </Card>
    );
  }

  const runway = p.runwayDays;
  const gauge = runway == null ? (p.balanceUsd != null && p.balanceUsd > 0 ? 1 : 0) : Math.min(runway / GAUGE_DAYS, 1);
  const runwayText =
    runway == null ? t('ADMIN_COSTS.CARD.RUNWAY_NONE') : runway > 90 ? t('ADMIN_COSTS.CARD.RUNWAY_LONG') : t('ADMIN_COSTS.CARD.RUNWAY', { days: runway < 10 ? runway.toFixed(1) : Math.round(runway) });
  const running = p.running || [];
  const pace = Math.max(p.avgDailyUsd, (p.burnPerHourUsd || 0) * 24);

  return (
    <Card className="h-full gap-0 px-4 py-4" data-testid={`costs-provider-${p.provider}`}>
      {header}

      <div className="mt-4">
        <p className="text-xs text-muted-foreground">{t('ADMIN_COSTS.CARD.BALANCE')}</p>
        {p.balanceUsd != null ? (
          <p className="mt-1 font-heading text-2xl leading-none font-medium tracking-tight tabular-nums" data-testid={`costs-balance-${p.provider}`}>
            {moneyShort(p.balanceUsd)}
          </p>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">{p.provider === 'azure' ? t('ADMIN_COSTS.CARD.SET_CREDIT') : t('ADMIN_COSTS.CARD.NO_BALANCE')}</p>
        )}
        {p.balanceSource === 'manual' && <p className="mt-1 text-[11px] text-muted-foreground">{t('ADMIN_COSTS.CARD.MANUAL')}</p>}
      </div>

      {p.balanceUsd != null && (
        <div className="mt-3">
          <div className="h-1.5 overflow-hidden rounded-full bg-muted" role="meter" aria-valuemin={0} aria-valuemax={GAUGE_DAYS} aria-valuenow={runway ?? undefined} aria-label={runwayText}>
            <div className={cn('h-full rounded-full transition-[width]', BAR[p.level])} style={{ width: `${Math.max(gauge * 100, p.balanceUsd > 0 ? 3 : 0)}%` }} />
          </div>
          <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-muted-foreground tabular-nums">
            <span>{runwayText}</span>
            {p.thresholdUsd != null && !p.autoReload && <span>{t('ADMIN_COSTS.CARD.THRESHOLD', { amount: money(p.thresholdUsd) })}</span>}
          </div>
        </div>
      )}

      <dl className="mt-4 grid grid-cols-3 gap-2 text-xs">
        <div>
          <dt className="text-muted-foreground">{t('ADMIN_COSTS.CARD.TODAY')}</dt>
          <dd className="mt-0.5 font-medium tabular-nums">{p.todayUsd == null ? '—' : money(p.todayUsd)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('ADMIN_COSTS.CARD.MONTH')}</dt>
          <dd className="mt-0.5 font-medium tabular-nums">{moneyShort(p.monthUsd)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('ADMIN_COSTS.CARD.PACE')}</dt>
          <dd className="mt-0.5 font-medium tabular-nums">{t('ADMIN_COSTS.CARD.PER_DAY', { amount: moneyShort(pace) })}</dd>
        </div>
      </dl>

      {p.trend.length > 1 && <Sparkline data={p.trend} className="mt-3 h-8" />}

      <div className="mt-auto flex flex-col gap-1 pt-3 text-[11px] text-muted-foreground">
        {running.length > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <Cpu className="size-3" />
            {t('ADMIN_COSTS.CARD.RUNNING', { count: running.length, amount: money(running.reduce((s, r) => s + r.usdPerHour, 0)) })}
          </span>
        )}
        {p.autoReload && (
          <span className="inline-flex items-center gap-1.5">
            <RefreshCcw className="size-3" />
            {t('ADMIN_COSTS.CARD.RELOAD', { amount: money(p.autoReload.amountUsd), threshold: money(p.autoReload.thresholdUsd) })}
          </span>
        )}
        {p.provider === 'azure' && <span>{t('ADMIN_COSTS.CARD.LAGS')}</span>}
        {p.error && <span className="text-destructive">{t('ADMIN_COSTS.CARD.ERROR', { error: p.error })}</span>}
      </div>
    </Card>
  );
}
